import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import "./PlacementTestStart.css";

function safeTrim(value) {
  return String(value ?? "").trim();
}

function storageKey(programSlug) {
  return `casaclass:placementLead:${programSlug || "default"}`;
}

export function loadPlacementLead(programSlug) {
  try {
    const raw = localStorage.getItem(storageKey(programSlug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function savePlacementLead(programSlug, lead) {
  try {
    localStorage.setItem(storageKey(programSlug), JSON.stringify(lead));
  } catch {
    // ignore
  }
}

export function clearPlacementLead(programSlug) {
  try {
    localStorage.removeItem(storageKey(programSlug));
  } catch {
    // ignore
  }
}

export default function PlacementTestStart() {
  const { accessToken } = useAuth();
  const { language, t } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const tFn = (key, options) => {
    try {
      return t(key, options);
    } catch {
      return options?.defaultValue || key;
    }
  };

  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search],
  );

  const programSlug = safeTrim(query.get("program") || "");
  const testId = safeTrim(query.get("testId") || "");

  useEffect(() => {
    if (programSlug) return;
    navigate("/placement-test", { replace: true });
  }, [navigate, programSlug]);

  // Guest-only page: if logged in, skip the lead form completely.
  useEffect(() => {
    if (!accessToken) return;
    if (!programSlug) return;
    if (testId) {
      navigate(
        `/placement-test/${encodeURIComponent(testId)}?program=${encodeURIComponent(programSlug)}`,
      );
    } else {
      navigate(`/placement-test?program=${encodeURIComponent(programSlug)}`);
    }
  }, [accessToken, navigate, programSlug, testId]);

  const existing = useMemo(
    () => (programSlug ? loadPlacementLead(programSlug) : null),
    [programSlug],
  );

  const [form, setForm] = useState({
    child_full_name: safeTrim(existing?.child_full_name),
    child_age: safeTrim(existing?.child_age),
    child_grade: safeTrim(existing?.child_grade),
    parent_full_name: safeTrim(existing?.parent_full_name),
    parent_phone: safeTrim(existing?.parent_phone),
    parent_email: safeTrim(existing?.parent_email),
    city: safeTrim(existing?.city),
    notes: safeTrim(existing?.notes),
    consent: Boolean(existing?.consent ?? true),
  });

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateField = (name, value) => {
    setField(name, value);
    const key =
      name === "parent_phone" || name === "parent_email" ? "contact" : name;
    setFieldErrors((prev) => prev.filter((x) => x !== key));
  };

  const validate = () => {
    const errors = [];
    if (!safeTrim(form.child_full_name)) errors.push("child_full_name");
    if (!safeTrim(form.parent_full_name)) errors.push("parent_full_name");
    if (!safeTrim(form.parent_phone) && !safeTrim(form.parent_email)) {
      errors.push("contact");
    }
    if (!form.consent) errors.push("consent");
    return errors;
  };

  const [fieldErrors, setFieldErrors] = useState([]);
  const hasError = (name) => fieldErrors.includes(name);

  const onSubmit = (e) => {
    e.preventDefault();
    setFieldErrors([]);

    if (!programSlug) {
      navigate("/placement-test", { replace: true });
      return;
    }

    const errs = validate();
    if (errs.length) {
      setFieldErrors(errs);
      return;
    }

    const payload = {
      child_full_name: safeTrim(form.child_full_name),
      child_age: safeTrim(form.child_age),
      child_grade: safeTrim(form.child_grade),
      parent_full_name: safeTrim(form.parent_full_name),
      parent_phone: safeTrim(form.parent_phone),
      parent_email: safeTrim(form.parent_email),
      city: safeTrim(form.city),
      notes: safeTrim(form.notes),
      consent: Boolean(form.consent),
      program: programSlug,
      created_at: new Date().toISOString(),
    };

    savePlacementLead(programSlug, payload);

    if (testId) {
      navigate(
        `/placement-test/${encodeURIComponent(testId)}?program=${encodeURIComponent(programSlug)}`,
      );
      return;
    }

    navigate(`/placement-test?program=${encodeURIComponent(programSlug)}`);
  };

  return (
    <main className="placement-start" dir={dir}>
      <div className="pp-shell placement-start__shell">
        <header className="placement-start__header">
          <div>
            <span className="pp-section-tag pp-section-tag--desert">
              {tFn("testsModule.placement.title", {
                defaultValue: "Placement test",
              })}
            </span>
            <h1 className="placement-start__title">
              {tFn("testsModule.placement.startForm.title", {
                defaultValue: "Child information",
              })}
            </h1>
            <p className="placement-start__subtitle">
              {tFn("testsModule.placement.startForm.subtitle", {
                defaultValue:
                  "We’ll send the results to the teacher so they can contact you.",
              })}
            </p>
          </div>
          <div className="placement-start__actions">
            <Link
              className="btn ghost"
              to={
                programSlug
                  ? `/placement-test?program=${encodeURIComponent(programSlug)}`
                  : "/placement-test"
              }
            >
              {tFn("testsModule.common.back", { defaultValue: "Back" })}
            </Link>
          </div>
        </header>


        <form className="placement-start__card" onSubmit={onSubmit}>
          <div className="placement-start__grid">
            <label
              className={`placement-start__field${hasError("child_full_name") ? " is-bad" : ""}`}
            >
              <span>
                {tFn("testsModule.placement.startForm.childFullName", {
                  defaultValue: "Child full name",
                })}{" "}
                *
              </span>
              <input
                value={form.child_full_name}
                aria-invalid={hasError("child_full_name") || undefined}
                autoComplete="name"
                onChange={(e) => updateField("child_full_name", e.target.value)}
              />
            </label>

            <label className="placement-start__field">
              <span>
                {tFn("testsModule.placement.startForm.childAge", {
                  defaultValue: "Age",
                })}
              </span>
              <input
                value={form.child_age}
                onChange={(e) => updateField("child_age", e.target.value)}
              />
            </label>

            <label className="placement-start__field">
              <span>
                {tFn("testsModule.placement.startForm.childGrade", {
                  defaultValue: "Grade/Level",
                })}
              </span>
              <input
                value={form.child_grade}
                onChange={(e) => updateField("child_grade", e.target.value)}
              />
            </label>

            <label
              className={`placement-start__field${hasError("parent_full_name") ? " is-bad" : ""}`}
            >
              <span>
                {tFn("testsModule.placement.startForm.parentFullName", {
                  defaultValue: "Parent full name",
                })}{" "}
                *
              </span>
              <input
                value={form.parent_full_name}
                aria-invalid={hasError("parent_full_name") || undefined}
                autoComplete="name"
                onChange={(e) => updateField("parent_full_name", e.target.value)}
              />
            </label>

            <label
              className={`placement-start__field${hasError("contact") ? " is-bad" : ""}`}
            >
              <span>
                {tFn("testsModule.placement.startForm.parentPhone", {
                  defaultValue: "Phone",
                })}
              </span>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                dir="ltr"
                value={form.parent_phone}
                onChange={(e) => updateField("parent_phone", e.target.value)}
              />
            </label>

            <label
              className={`placement-start__field${hasError("contact") ? " is-bad" : ""}`}
            >
              <span>
                {tFn("testsModule.placement.startForm.parentEmail", {
                  defaultValue: "Email",
                })}
              </span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                dir="ltr"
                value={form.parent_email}
                onChange={(e) => updateField("parent_email", e.target.value)}
              />
            </label>

            <label className="placement-start__field">
              <span>
                {tFn("testsModule.placement.startForm.city", {
                  defaultValue: "City",
                })}
              </span>
              <input
                value={form.city}
                onChange={(e) => updateField("city", e.target.value)}
              />
            </label>

            <p className="placement-start__hint placement-start__field--full">
              {tFn("testsModule.placement.startForm.contactHint", {
                defaultValue: "* Required. Give at least a phone number or an email.",
              })}
            </p>

            <label className="placement-start__field placement-start__field--full">
              <span>
                {tFn("testsModule.placement.startForm.notes", {
                  defaultValue: "Notes",
                })}
              </span>
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
              />
            </label>
          </div>

          <label
            className={`placement-start__consent${hasError("consent") ? " is-bad" : ""}`}
          >
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => updateField("consent", e.target.checked)}
            />
            <span>
              {tFn("testsModule.placement.startForm.consent", {
                defaultValue:
                  "I agree to send my info and test results to the teacher for contact.",
              })}
            </span>
          </label>

          {fieldErrors.length ? (
            <ul className="placement-start__errors" role="alert">
              {fieldErrors.map((key) => (
                <li key={key}>
                  {tFn(`testsModule.placement.startForm.errors.${key}`, {
                    defaultValue: key,
                  })}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="placement-start__submit">
            <button className="btn primary" type="submit">
              {tFn("testsModule.placement.startForm.continue", {
                defaultValue: "Continue",
              })}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
